/**
 * 用户个人资料弹窗组件
 */

import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Space, message, Divider, Card, Row, Col, Avatar, Typography } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons';
import { useAuth } from '../../../../hooks/useAuth';
import { userService } from '../../../../services/userService';
import { handleApiError, handleApiResponse } from '../../../../utils/errorHandler';

const { Text, Title } = Typography;

interface UserProfileModalProps {
  visible: boolean;
  onClose: () => void;
}

interface UserInfo {
  id: number;
  username: string;
  nickname: string;
  email?: string;
  phone?: string;
  status: number;
  lastLoginTime?: string;
  createTime?: string;
}

interface ChangePasswordForm {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const UserProfileModal: React.FC<UserProfileModalProps> = ({ visible, onClose }) => {
  const { user } = useAuth();
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();

  // 加载用户信息
  const loadUserInfo = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      const response = await userService.getById(user.id);
      if (response.code === 200 && response.data) {
        setUserInfo(response.data);
        profileForm.setFieldsValue(response.data);
      } else {
        // 显示后台返回的具体错误信息
        handleApiError(response, '加载用户信息失败');
      }
    } catch (error: any) {
      handleApiError(error, '加载用户信息失败');
    } finally {
      setLoading(false);
    }
  };

  // 修改密码
  const handleChangePassword = async (values: ChangePasswordForm) => {
    if (values.newPassword !== values.confirmPassword) {
      message.error('两次输入的新密码不一致');
      return;
    }

    if (!user?.id) {
      message.error('用户信息不存在');
      return;
    }

    setPasswordLoading(true);
    try {
      const response = await userService.changePassword({
        userId: user.id,
        oldPassword: values.oldPassword,
        newPassword: values.newPassword
      });
      
      if (handleApiResponse(response, '密码修改成功')) {
        passwordForm.resetFields();
        setShowPasswordForm(false);
      }
    } catch (error: any) {
      handleApiError(error, '修改密码失败，请重试');
    } finally {
      setPasswordLoading(false);
    }
  };

  // 更新用户信息
  const handleUpdateProfile = async (values: any) => {
    if (!userInfo) return;
    
    setLoading(true);
    try {
      const response = await userService.update({
        ...userInfo,
        ...values
      });
      
      if (handleApiResponse(response, '个人信息更新成功')) {
        loadUserInfo(); // 重新加载用户信息
      }
    } catch (error: any) {
      handleApiError(error, '更新失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible && user?.id) {
      loadUserInfo();
    }
  }, [visible, user?.id]);

  const handleCancel = () => {
    profileForm.resetFields();
    passwordForm.resetFields();
    setShowPasswordForm(false);
    onClose();
  };

  return (
    <Modal
      title="个人资料"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
      destroyOnHidden
    >
      <div style={{ padding: '20px 0' }}>
        {/* 用户基本信息卡片 */}
        <Card 
          title={
            <Space>
              <UserOutlined />
              <span>基本信息</span>
            </Space>
          }
          style={{ marginBottom: 16 }}
        >
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <Avatar 
                  size={80} 
                  icon={<UserOutlined />}
                  style={{ backgroundColor: '#1890ff' }}
                />
                <div style={{ marginTop: 8 }}>
                  <Title level={4} style={{ margin: 0 }}>
                    {userInfo?.nickname || userInfo?.username || '用户'}
                  </Title>
                  <Text type="secondary">
                    {userInfo?.username}
                  </Text>
                </div>
              </div>
            </Col>
          </Row>

          <Form
            form={profileForm}
            layout="vertical"
            onFinish={handleUpdateProfile}
            initialValues={userInfo || undefined}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="用户名"
                  name="username"
                  rules={[{ required: true, message: '请输入用户名' }]}
                >
                  <Input disabled />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="昵称"
                  name="nickname"
                  rules={[{ required: true, message: '请输入昵称' }]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="邮箱"
                  name="email"
                  rules={[
                    { type: 'email', message: '请输入正确的邮箱格式' }
                  ]}
                >
                  <Input prefix={<MailOutlined />} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="手机号"
                  name="phone"
                  rules={[
                    { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号' }
                  ]}
                >
                  <Input prefix={<PhoneOutlined />} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="状态">
                  <Text type={userInfo?.status === 1 ? 'success' : 'danger'}>
                    {userInfo?.status === 1 ? '正常' : '禁用'}
                  </Text>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="最后登录时间">
                  <Text type="secondary">
                    {userInfo?.lastLoginTime 
                      ? new Date(userInfo.lastLoginTime).toLocaleString('zh-CN')
                      : '从未登录'
                    }
                  </Text>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={handleCancel}>
                  取消
                </Button>
                <Button type="primary" htmlType="submit" loading={loading}>
                  保存
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>

        {/* 修改密码卡片 */}
        <Card 
          title={
            <Space>
              <LockOutlined />
              <span>修改密码</span>
            </Space>
          }
        >
          {!showPasswordForm ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <Text type="secondary">点击下方按钮修改登录密码</Text>
              <div style={{ marginTop: 16 }}>
                <Button 
                  type="primary" 
                  icon={<LockOutlined />}
                  onClick={() => setShowPasswordForm(true)}
                >
                  修改密码
                </Button>
              </div>
            </div>
          ) : (
            <Form
              form={passwordForm}
              layout="vertical"
              onFinish={handleChangePassword}
            >
              <Form.Item
                label="当前密码"
                name="oldPassword"
                rules={[{ required: true, message: '请输入当前密码' }]}
              >
                <Input.Password prefix={<LockOutlined />} />
              </Form.Item>

              <Form.Item
                label="新密码"
                name="newPassword"
                rules={[
                  { required: true, message: '请输入新密码' },
                  { min: 6, message: '密码长度不能少于6位' }
                ]}
              >
                <Input.Password prefix={<LockOutlined />} />
              </Form.Item>

              <Form.Item
                label="确认新密码"
                name="confirmPassword"
                rules={[
                  { required: true, message: '请确认新密码' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('两次输入的密码不一致'));
                    },
                  }),
                ]}
              >
                <Input.Password prefix={<LockOutlined />} />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Space>
                  <Button onClick={() => {
                    setShowPasswordForm(false);
                    passwordForm.resetFields();
                  }}>
                    取消
                  </Button>
                  <Button type="primary" htmlType="submit" loading={passwordLoading}>
                    确认修改
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          )}
        </Card>
      </div>
    </Modal>
  );
};

export default UserProfileModal;
